import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { Card, CardContent } from "@/components/ui/card";
import { ArrowLeft, Upload, Search, Phone, Bell, BarChart3, Headphones, Shield } from "lucide-react";
import { Button } from "@/components/ui/button";
import PlanSelector from "@/components/upgrade/PlanSelector";
import TrialStatusBanner from "@/components/upgrade/TrialStatusBanner";
import UpgradePaymentForm from "@/components/upgrade/UpgradePaymentForm";
import { PromoCodeInput } from "@/components/promo/PromoCodeInput";
import { usePageTitle } from "@/hooks/usePageTitle";
import { useSubscription } from "@/contexts/SubscriptionContext";
import TourWrapper from "@/components/onboarding/TourWrapper";
import { sellerUpgradeSteps } from "@/config/tours/sellerUpgradeTour";
import { safeStorage } from "@/utils/safariStorage";

const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY);

const SellerUpgradePage = () => {
  usePageTitle("Upgrade to Shark Plan");
  const navigate = useNavigate();
  const location = useLocation();
  const { checkSubscription } = useSubscription();
  const [selectedPlan, setSelectedPlan] = useState<'monthly' | 'annual'>('annual');
  const [appliedPromoCode, setAppliedPromoCode] = useState<string | null>(null);
  const [promoDiscount, setPromoDiscount] = useState<string | null>(null);
  const [runTour, setRunTour] = useState(false);

  const tourId = 'seller_upgrade';

  useEffect(() => {
    const hasSeenTour = safeStorage.getItem(`godyspo_tour_${tourId}`) === 'true';
    const shouldStartTour = location.state?.startTour === tourId;

    if (!hasSeenTour || shouldStartTour) {
      const checkAndStartTour = (retries = 5) => {
        const featureList = document.querySelector('[data-tour="feature-list"]');
        if (featureList) {
          setTimeout(() => setRunTour(true), 300);
        } else if (retries > 0) {
          setTimeout(() => checkAndStartTour(retries - 1), 200);
        }
      };
      checkAndStartTour();
    }
  }, [location.state]);

  const handleTourComplete = () => {
    safeStorage.setItem(`godyspo_tour_${tourId}`, 'true');
    setRunTour(false);
  };

  const handleTourSkip = () => {
    safeStorage.setItem(`godyspo_tour_${tourId}`, 'true');
    setRunTour(false);
  };

  const features = [
    {
      icon: <Upload className="h-6 w-6 text-godyspo-gold" />,
      title: "Unlimited Property Listings",
      description: "List as many wholesale deals as you want with no restrictions"
    },
    {
      icon: <Search className="h-6 w-6 text-godyspo-gold" />,
      title: "Advanced Buyer Search",
      description: "Find qualified buyers with powerful filtering by markets, budget, and experience"
    },
    {
      icon: <Phone className="h-6 w-6 text-godyspo-gold" />,
      title: "Direct Buyer Contact",
      description: "Access phone numbers and emails of buyers who view your properties"
    },
    {
      icon: <Bell className="h-6 w-6 text-godyspo-gold" />,
      title: "Real-Time Notifications",
      description: "Know instantly when buyers view your listings or show interest"
    },
    {
      icon: <BarChart3 className="h-6 w-6 text-godyspo-gold" />,
      title: "Buyer List Management",
      description: "Organize and segment your buyer database for targeted outreach"
    },
    {
      icon: <Shield className="h-6 w-6 text-godyspo-gold" />,
      title: "Analytics & Insights",
      description: "Track property views, buyer engagement, and optimize your listings"
    },
    {
      icon: <Headphones className="h-6 w-6 text-godyspo-gold" />,
      title: "Priority Support",
      description: "Get dedicated help from our team to maximize your success"
    }
  ];

  const handleSuccess = async () => {
    // Wait for Stripe webhook to process
    await new Promise(resolve => setTimeout(resolve, 500));
    
    // Refresh subscription status
    await checkSubscription();
    
    // Navigate to dashboard with updated status
    navigate('/seller/dashboard');
  };

  const handleError = (error: string) => {
    console.error('Upgrade error:', error);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#080c14] to-[#0a0f1a] py-8">
      <TourWrapper
        steps={sellerUpgradeSteps}
        run={runTour}
        onComplete={handleTourComplete}
        onSkip={handleTourSkip}
      />
      <div className="container mx-auto px-4 max-w-6xl">
        {/* Back Button */}
        <Button
          variant="ghost"
          onClick={() => navigate('/seller/dashboard')}
          className="mb-6 text-white hover:text-godyspo-gold"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Dashboard
        </Button>

        {/* Trial Status Banner */}
        <TrialStatusBanner />

        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Upgrade to <span className="text-godyspo-gold">Shark Plan</span>
          </h1>
          <p className="text-xl text-gray-300 max-w-2xl mx-auto">
            Connect with more qualified buyers and close deals faster than ever before
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8">
          {/* Left Column - Features */}
          <div className="space-y-6">
            <h2 className="text-2xl font-bold text-white mb-6">What You'll Get</h2>
            
            <div className="space-y-4" data-tour="feature-list">
              {features.map((feature, index) => (
                <Card key={index} className="bg-card/50 border-muted">
                  <CardContent className="p-4 flex gap-4">
                    <div className="flex-shrink-0">
                      {feature.icon}
                    </div>
                    <div>
                      <h3 className="font-semibold text-white mb-1">{feature.title}</h3>
                      <p className="text-sm text-muted-foreground">{feature.description}</p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* ROI Callout */}
            <Card className="bg-godyspo-gold/10 border-godyspo-gold" data-tour="roi-callout">
              <CardContent className="p-6">
                <h3 className="text-xl font-bold text-godyspo-gold mb-3">
                  ROI in Just One Deal
                </h3>
                <p className="text-white mb-4">
                  Close just one additional deal per year and the Shark Plan pays for itself many times over.
                </p>
                <div className="space-y-2 text-sm text-white">
                  <p>✓ Cancel anytime, no long-term contracts</p>
                  <p>✓ Secure payment processing</p>
                  <p>✓ Join 500+ successful wholesalers</p>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Right Column - Payment Form */}
          <div className="space-y-6">
            <Card className="bg-card border-muted">
              <CardContent className="p-6">
                <div data-tour="plan-selector">
                  <PlanSelector
                    userType="seller"
                    selectedPlan={selectedPlan}
                    onPlanChange={setSelectedPlan}
                  />
                </div>
                
                <div className="mt-8 pt-6 border-t border-muted">
                  <div data-tour="promo-code">
                    <PromoCodeInput
                      onPromoApplied={(code, discount) => {
                        setAppliedPromoCode(code);
                        setPromoDiscount(discount);
                      }}
                      onPromoRemoved={() => {
                        setAppliedPromoCode(null);
                        setPromoDiscount(null);
                      }}
                      userType="seller"
                      planType={selectedPlan}
                    />
                  </div>
                  
                  <div className="mt-6" data-tour="payment-form">
                    <Elements stripe={stripePromise}>
                      <UpgradePaymentForm
                        userType="seller"
                        selectedPlan={selectedPlan}
                        onSuccess={handleSuccess}
                        onError={handleError}
                        promoCode={appliedPromoCode || undefined}
                      />
                    </Elements>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SellerUpgradePage;
